import React from 'react';
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from '@mui/material';

export type BranchPageTemplate = 'province-map' | 'classic';

export function normalizeBranchPageTemplate(value?: unknown): BranchPageTemplate {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'province-map' || raw === 'provincemap' || raw === 'new') {
    return 'province-map';
  }
  return 'classic';
}

interface TemplateOption {
  id: BranchPageTemplate;
  label: string;
  description: string;
  preview: React.ReactNode;
}

const buildTemplateOptions = (mapAccentColor?: string): TemplateOption[] => {
  const mapFill = mapAccentColor || '#94a3b8';
  return [
  {
    id: 'province-map',
    label: 'İl Haritası',
    description: 'İstatistik kartları, boyalı Türkiye haritası, arama ve şube kartları',
    preview: (
      <Box sx={{ p: 1, height: '100%', bgcolor: '#f3f4f6', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4 }}>
          <Box sx={{ height: 18, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ height: 8, width: 16, bgcolor: mapFill, borderRadius: 0.3, mt: 0.6, ml: 0.5 }} />
          </Box>
          <Box sx={{ height: 18, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #e5e7eb' }}>
            <Box sx={{ height: 8, width: 12, bgcolor: '#334155', borderRadius: 0.3, mt: 0.6, ml: 0.5 }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#fff', borderRadius: 0.5, border: '1px solid #e5e7eb', p: 0.4 }}>
          <Box sx={{ height: '100%', bgcolor: mapFill, borderRadius: 0.4, opacity: 0.55 }} />
        </Box>
        <Box sx={{ height: 10, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb' }} />
      </Box>
    ),
  },
  {
    id: 'classic',
    label: 'Klasik',
    description: 'Pin harita, şehir filtresi ve altta tüm şube kartları',
    preview: (
      <Box sx={{ p: 1, height: '100%', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ height: 6, width: 36, bgcolor: '#111827', borderRadius: 0.3, mx: 'auto', mb: 0.3 }} />
          <Box sx={{ height: 4, width: 56, bgcolor: '#94a3b8', borderRadius: 0.3, mx: 'auto' }} />
        </Box>
        <Box sx={{ flex: 1, bgcolor: '#e2e8f0', borderRadius: 0.4, position: 'relative' }}>
          {[
            { top: '28%', left: '32%' },
            { top: '40%', left: '48%' },
            { top: '36%', left: '58%' },
          ].map((pin, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                top: pin.top,
                left: pin.left,
                width: 6,
                height: 6,
                bgcolor: mapFill,
                borderRadius: '50%',
              }}
            />
          ))}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.35 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                height: 10,
                bgcolor: i === 1 ? mapFill : '#e2e8f0',
                borderRadius: 0.3,
              }}
            />
          ))}
        </Box>
      </Box>
    ),
  },
];
};

interface BranchTemplatePickerProps {
  value: BranchPageTemplate;
  onChange: (templateType: BranchPageTemplate) => void;
  mapAccentColor?: string;
}

const BranchTemplatePicker: React.FC<BranchTemplatePickerProps> = ({ value, onChange, mapAccentColor }) => {
  const options = buildTemplateOptions(mapAccentColor);
  return (
  <Grid container spacing={2}>
    {options.map((option) => {
      const selected = value === option.id;
      return (
        <Grid item xs={12} sm={6} key={option.id}>
          <Card
            variant={selected ? 'outlined' : 'elevation'}
            sx={{
              height: '100%',
              border: selected ? '2px solid' : '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
            }}
          >
            <CardActionArea onClick={() => onChange(option.id)} sx={{ height: '100%' }}>
              <Box
                sx={{
                  height: 120,
                  bgcolor: '#fafafa',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {option.preview}
              </Box>
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" fontSize="0.85rem">
                  {option.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35, display: 'block' }}>
                  {option.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      );
    })}
  </Grid>
  );
};

export default BranchTemplatePicker;
